import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { reverse } from 'named-urls'
import { routes } from 'routes'
import useSnackbar from 'hooks/useSnackbar'

import { Box, Chip, Link, CircularProgress } from '@material-ui/core/'

import { Container, ContentHeader, ClientInfo } from 'components'

import * as service from 'service'
import helpers from 'helpers'
import moment from 'moment'
import useFetch from 'hooks/useFetch'

import DialogTaxInvoiceCancel from 'views/Invoices/components/DialogTaxInvoiceCancel'

import { ContainerSummary, ItemSummary } from 'components/Summary'
import { Permitted } from 'components/Permissions'
import { LogButton } from 'components/Logs'
import Button from '@material-ui/core/Button'
import TurnedInNotIcon from '@material-ui/icons/TurnedInNot'
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined'
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined'
import TodayIcon from '@material-ui/icons/Today'
import BlockOutlined from '@material-ui/icons/BlockOutlined'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { AttachMoneyOutlined } from '@material-ui/icons'

const TaxInvoicesShow = ({ match }) => {
  const taxInvoiceId = match.params.taxInvoiceId
  const [dialogTaxInvoiceCancel, setDialogTaxInvoiceCancel] = React.useState(
    false,
  )
  const [customer, setCustomer] = React.useState({})
  const [pageLoader, setPageLoader] = React.useState(false)
  const snackbar = useSnackbar()

  const { response, isLoading, refresh } = useFetch(
    service.recurrence.taxInvoices.get,
    {
      taxInvoiceId,
    },
  )

  const serviceTaxInvoice = response && response.data.serviceTaxInvoice

  React.useEffect(() => {
    const loadCustomer = async customerId => {
      let response = await service.recurrence.customers.get({
        clientId: customerId,
      })
      setCustomer(response.data.customer)
    }
    if (serviceTaxInvoice) loadCustomer(serviceTaxInvoice.customer.id)
  }, [serviceTaxInvoice])

  const openTaxInvoice = async () => {
    setPageLoader(true)
    if (serviceTaxInvoice.urlInvoice) {
      setPageLoader(false)
      return window.open(serviceTaxInvoice.urlInvoice, '', 'resizable')
    }

    try {
      const response = await service.recurrence.taxInvoices.download({
        taxInvoiceId: serviceTaxInvoice.id,
      })

      const fileName = `nf-#${serviceTaxInvoice.nfId}_fatura-#${serviceTaxInvoice.invoiceId}.pdf`
      helpers.archives.downloadPdf(response.data, fileName)
    } catch {
      snackbar.open({
        message: 'Falha ao consultar nota fiscal!',
        variant: 'error',
      })
    } finally {
      refresh()
      setPageLoader(false)
    }
  }

  return (
    <Container>
      {pageLoader ? (
        <div>
          <CircularProgress />
        </div>
      ) : (
        <>
          <ContentHeader title="Nota Fiscal" subtitle="Detalhes">
            {serviceTaxInvoice && (
              <Box display="flex">
                {serviceTaxInvoice && (
                  <LogButton
                    to={reverse(routes.taxInvoices.logs, {
                      taxInvoiceId: serviceTaxInvoice.id,
                    })}
                  />
                )}

                {!helpers.taxInvoices.statusVerification.isFailed(
                  serviceTaxInvoice.status,
                ) && (
                  <Permitted tag="download_service_tax_invoice">
                    <Box ml={2}>
                      <Button
                        variant="text"
                        color="default"
                        onClick={() => openTaxInvoice()}
                      >
                        Consultar
                      </Button>
                    </Box>
                  </Permitted>
                )}
                {helpers.taxInvoices.statusVerification.isIssued(
                  serviceTaxInvoice.status,
                ) && (
                  <Permitted tag="cancel_service_tax_invoice">
                    <Box ml={2}>
                      <Button
                        variant="text"
                        color="default"
                        onClick={() => setDialogTaxInvoiceCancel(true)}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Permitted>
                )}
                <Permitted tag="attach_service_tax_invoice">
                  <Box ml={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      component={RouterLink}
                      to={reverse(routes.taxInvoices.edit, { taxInvoiceId })}
                    >
                      Editar
                    </Button>
                  </Box>
                </Permitted>
              </Box>
            )}
          </ContentHeader>
          <Box mb={4}>
            <ContainerSummary isLoading={isLoading}>
              <ItemSummary
                title="Identificador"
                icon={<TurnedInNotIcon color="disabled" />}
                value={serviceTaxInvoice && serviceTaxInvoice.id}
              />
              {serviceTaxInvoice && serviceTaxInvoice.issuedAt && (
                <ItemSummary
                  title="Número da nota"
                  icon={<ReceiptOutlinedIcon color="disabled" />}
                  value={serviceTaxInvoice.nfId}
                />
              )}
              <ItemSummary
                title="Fatura"
                icon={<MonetizationOnOutlinedIcon color="disabled" />}
                value={
                  <Link
                    component={RouterLink}
                    to={reverse(routes.invoices.show, {
                      invoiceId:
                        serviceTaxInvoice && serviceTaxInvoice.invoiceToken,
                    })}
                  >
                    {serviceTaxInvoice && serviceTaxInvoice.invoiceId}
                  </Link>
                }
              />
              {serviceTaxInvoice && serviceTaxInvoice.issuedAt && (
                <ItemSummary
                  title="Emissão"
                  icon={<TodayIcon color="disabled" />}
                  value={moment(serviceTaxInvoice.issuedAt)
                    .utc(false)
                    .format('DD/MM/YYYY')}
                />
              )}
              {helpers.taxInvoices.statusVerification.isCanceled(
                serviceTaxInvoice && serviceTaxInvoice.status,
              ) && (
                <ItemSummary
                  title="Cancelamento"
                  icon={<BlockOutlined color="disabled" />}
                  value={moment(
                    serviceTaxInvoice && serviceTaxInvoice.canceledAt,
                  )
                    .utc(false)
                    .format('DD/MM/YYYY')}
                />
              )}
              <ItemSummary
                title="Valor"
                icon={<AttachMoneyOutlined color="disabled" />}
                value={helpers.currency(
                  serviceTaxInvoice && serviceTaxInvoice.amount,
                )}
              />
              <ItemSummary
                title="Status"
                icon={<ErrorOutlineIcon color="disabled" />}
                value={
                  <Chip
                    label={helpers.taxInvoices.status(
                      serviceTaxInvoice && serviceTaxInvoice.status,
                    )}
                    size="small"
                    style={{
                      backgroundColor: helpers.taxInvoices.statusColor(
                        serviceTaxInvoice && serviceTaxInvoice.status,
                      ),
                      color: 'white',
                    }}
                  />
                }
              />
            </ContainerSummary>
          </Box>

          <Box mb={4}>
            {!isLoading && customer && <ClientInfo customer={customer} />}
          </Box>

          {!isLoading && serviceTaxInvoice && (
            <DialogTaxInvoiceCancel
              open={dialogTaxInvoiceCancel}
              setOpen={setDialogTaxInvoiceCancel}
              onEvent={refresh}
              onLoading={setPageLoader}
              taxInvoiceId={serviceTaxInvoice.id}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default TaxInvoicesShow
